import { generateTimeline } from '@desygner/ui-common-animation-timeline';

import { logError } from 'src/utils/logger';

import { PageContentFactory } from '../../../factories/page-content';

export const initiateAnimationPreview = () => {
  window.customElements.define('sidebar-animation-preview', SidebarAnimation);
};

class SidebarAnimation extends HTMLElement {
  pages = {};

  #pageContentFactory = new PageContentFactory();

  constructor () {
    super();
    this.attachShadow({ mode: 'open' });
  }

  render () {
    return `
      <div
        class="sidebar-animation"
        style="${INKIVE.C.IS_MOBILE ? 'visibility: hidden;' : ''}"
      ></div>
    `;
  }

  updateWrapperDimensions (wrapper, canvasBackground, preparedPageSvg, width) {
    const {
      width: newWidth,
      height: newHeight,
      top: newTop,
      left: newLeft
    } = canvasBackground.getBoundingClientRect();
    const scale = newWidth / width;

    wrapper.style.width = `${newWidth}px`;
    wrapper.style.height = `${newHeight}px`;
    wrapper.style.top = `${newTop}px`;
    wrapper.style.left = `${newLeft}px`;

    preparedPageSvg.style.transform = `scale(${scale})`;
  }

  observeCanvasChanges (wrapper, canvasBackground, preparedPageSvg, width) {
    const mutationObserver = new MutationObserver(() =>
      this.updateWrapperDimensions(
        wrapper,
        canvasBackground,
        preparedPageSvg,
        width
      )
    );
    mutationObserver.observe(canvasBackground, {
      attributes: true,
      attributeFilter: ['x', 'y', 'style']
    });

    wrapper.addEventListener('animationend', () => {
      mutationObserver.disconnect();
    });

    wrapper.addEventListener('click', () => {
      mutationObserver.disconnect();
      this.removeOldAnimationCanvas();
    });
  }

  async _createAnimationCanvas () {
    const canvasBackground = document.querySelector('#canvasBackground');
    const { width, height, top, left } = canvasBackground.getBoundingClientRect();

    const wrapper = document.createElement('div');
    wrapper.className = 'animation-canvas-wrapper';
    wrapper.style.cssText = `
      width: ${width}px;
      height: ${height}px;
      top: ${top}px;
      left: ${left}px;
      position: fixed;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
      z-index: 1;
      opacity: 0;
      overflow: hidden;
    `;

    const id = await INKIVE.PageFactory.getActivePageId();
    const pageSvg = await this.#pageContentFactory.getSvgElement(id);

    this.#pageContentFactory.setSvgElementDimensions(pageSvg, {
      height,
      width
    });

    wrapper.appendChild(pageSvg);
    this.shadowRoot.appendChild(wrapper);

    wrapper.style.opacity = 1;
    this.observeCanvasChanges(wrapper, canvasBackground, pageSvg, width);

    return wrapper;
  }

  removeOldAnimationCanvas () {
    const oldAnimationCanvas = this.shadowRoot.querySelector(
      '.animation-canvas-wrapper'
    );
    if (oldAnimationCanvas) {
      // Remove targets from all active animations
      this.timeline?.kill();

      // Remove old canvas
      oldAnimationCanvas.remove();
    }
  }

  async playCurrentAnimationOnCurrentPage (onlyAnimateSelectedElement) {
    const pageProperties = INKIVE.AnimationFactory.getPageConfiguration();
    this.removeOldAnimationCanvas();

    const svgElement = await this._createAnimationCanvas();
    this.timeline = generateTimeline(pageProperties, svgElement, {
      onlyAnimateSelectedElement
    });

    this.timeline?.play();
    const start = new Date();

    try {
      await new Promise((resolve) => {
        this.timeline.eventCallback('onComplete', () => {
          resolve();
        });
      });
    } catch (error) {
      logError('playCurrentAnimationOnCurrentPage', error);
    }

    if (INKIVE.C.DEBUG) {
      console.log(
        'Animation duration',
        new Date().getTime() - start.getTime(),
        this.timeline?.duration()
      );
    }

    svgElement.dispatchEvent(new Event('animationend'));
    this.removeOldAnimationCanvas();
  }
}
